/**
 * 特殊マス（西・北まわり）のデータ
 *
 * 担当: 飢饉(18) / 評定(22) / 敦賀湊(25) / 厳島(31) / 博多湊(33) / 高野山(39)
 *
 * いずれも国マスではないので Province ではなく SquareBase を用いる。
 * 石高・防御・地形は持たない。
 *
 * effects はルール実装側（packages/core）で扱うため、ここでは書かない。
 * 本ファイルの主眼は史実メモ（HistoryNote）である。
 *
 * 執筆基準は provinces/_reference.ts（尾張・甲斐）と
 * docs/05-学習設計.md §4「史実の扱いに関する原則」に従う。
 *   - 武将を英雄視・悪役視しない。戦闘行為を美化しない
 *   - 宗教勢力は事実の記述に留める
 *   - 女性・民衆の存在を消さない（合戦だけでなく暮らしの事実も書く）
 *   - 通説と異説がある事項は caveat に、ゲームの都合の改変は gameNote に必ず書く
 *
 * 年号は西暦を主とする。
 */

import type { SquareBase } from '../types';

/**
 * 18 飢饉（災害）。
 * 出羽(17) と越後(19) のあいだに置かれた災害マス。
 * 戦国時代を「合戦の時代」としてだけ見ないための一マス。
 */
export const KIKIN: SquareBase = {
  id: 18,
  kind: 'disaster',
  name: '飢饉',
  reading: 'ききん',
  region: 'tohoku',
  note: {
    summary:
      '冷害・干ばつ・長雨で作物が実らず、多くの人が飢えた。戦国時代の人々にとって、合戦より身近な脅威だった。',
    detail: {
      1: '天気がわるくて米や麦がみのらないことを飢饉といいます。むかしはたくわえが少なかったので、長雨や日照りが一年つづくだけで食べるものがなくなり、村の人たちは食べものをさがして町へ出ていきました。戦だけでなく、飢えも人々を苦しめました。',
      2: '冷害・干ばつ・長雨・虫害などで作物が実らず、広い範囲で食糧が不足する事態を飢饉という。1459年から1461年にかけての長禄・寛正の飢饉では、各地から京都へ流民が集まり、1461年の初めの二か月だけで八万二千人が亡くなったという記録が残る。時宗の僧である願阿弥が将軍足利義政の許しを得て六角堂の南に小屋を建て、粥を配って救おうとした。村では草の根や木の実を食べてしのぎ、それでも足りなければ土地を捨てて逃げた（逃散）。',
      3: '冷害・干ばつ・長雨・虫害・地震などで収穫が失われ、広範囲に食糧不足が及ぶ事態。中世は備蓄も流通も限られ、一年の不作が直ちに餓死につながった。長禄・寛正の飢饉（1459〜1461）では諸国から京都へ流民が押し寄せ、『碧山日録』は1461年の二か月ほどで八万二千人が死んだと記す。時宗の僧願阿弥は勧進の許可を得て六角堂の南に救済小屋を設け、一日八千人ほどに粥を施したが、一か月足らずで続かなくなり、鴨川の河原に遺体を葬って塚を築いたと伝わる。飢饉は暮らしの側だけの問題ではない。研究者は、不作の年に大名が出兵し、戦場で人や食糧を奪う「乱取り」が行われ、連れ去られた人が売買された事例を史料から指摘している。売られたのは多くが女性と子どもだった。飢饉のたびに徳政（借金の帳消し）を求める土一揆が起こり、寺社や大名による施行（せぎょう）も行われた。15〜16世紀は世界的に寒冷な時期だったとする気候研究もあるが、日本の記録は京都に偏っており、地方の実態は十分にわかっていない。',
    },
    year: [1459, 1461],
    keywords: ['飢饉', '長禄・寛正の飢饉', '願阿弥', '土一揆', '乱取り', '民衆'],
    caveat:
      '「八万二千人」は当時の日記『碧山日録』が伝える数で、正確な統計ではありません。飢饉の記録は京都のものが多く残る一方、地方の被害の広がりは史料が乏しく、全国でどれほどの人が亡くなったかはわかっていません。気候が寒冷だったとする説も、年輪や堆積物からの推定であり、年ごとの被害と直接結びつけられるわけではありません。',
    gameNote:
      '盤上では特定の一マスに止まると起こる出来事として扱っていますが、実際の飢饉は場所を選ばず、しかも数年にわたって続くものでした。ゲームの都合で「一回の出来事」に圧縮しています。',
  },
};

/**
 * 22 評定（イベント）。
 * 事件札を引くマス。会議で物事が決まっていたという史実そのものが主題。
 */
export const HYOJO: SquareBase = {
  id: 22,
  kind: 'council',
  name: '評定',
  reading: 'ひょうじょう',
  region: 'tokai',
  note: {
    summary:
      '重臣が集まって物事を決める会議。戦国大名の家では当主の独断ではなく、宿老たちの合議で方針が定まることが多かった。',
    detail: {
      1: '評定とは、大名とその家来たちが集まって話しあい、物事を決める会議のことです。戦をするか、和をむすぶか、どこに城をきずくかは、一人で決めるのではなく、みんなで相談して決めました。村でも「寄合」という話しあいがあり、百姓たちが自分たちの決まりをつくっていました。',
      2: '重臣が集まって政務や軍事を審議する会議。鎌倉幕府では1225年に北条泰時が評定衆を置き、合議で裁判や政務を決めた。室町幕府もこれを受け継いでいる。戦国大名の家中でも、宿老（年寄）による寄合や談合で方針が決まることが多く、当主が独断で動けたわけではない。今川氏の仮名目録（1526年）、伊達氏の塵芥集（1536年）、武田氏の甲州法度之次第（1547年）など、家ごとの法（分国法）もこうした場でまとめられた。村では乙名や年寄が寄合を開き、村掟を定めて自分たちで運営していた。',
      3: '政務・軍事・裁判を審議する合議の場。鎌倉幕府では1225年に北条泰時が評定衆を設け、執権の独裁ではなく合議で幕政を運ぶ形をとった。室町幕府もこれを継承したが、応仁の乱以降は実質を失っていく。戦国大名の家中では、宿老（年寄・老中）の寄合や談合が意思決定の中心で、当主の意向はしばしば重臣層との調整を必要とした。分国法もこの文脈で読める。1567年の六角氏式目は家臣たちが起草し、当主父子が承認する形をとっており、大名権力が家臣団の合意に支えられていたことを示す例として引かれる。惣村では乙名・年寄を中心に寄合が開かれ、用水・入会地・年貢の請負などを村掟として定めた。一揆は暴動ではなく「一味同心」を意味する語で、起請文を書き、その灰を混ぜた神水を回し飲みして（一味神水）対等な結合を確認する作法をともなった。意思決定に関わったのは男性だけではない。今川氏では氏親の妻寿桂尼が「歸」の印判を用いて文書を発給しており、当主の代替わりの前後に家の実務を担った女性の存在が確認できる。',
    },
    year: [1225, 1600],
    keywords: ['評定', '評定衆', '寄合', '分国法', '惣村', '一味同心'],
    caveat:
      '合議で決まったといっても、家によって当主と重臣の力関係は大きく違い、一様ではありません。「戦国大名は家臣の合意なしには動けなかった」という見方は近年強まっていますが、どこまで一般化できるかは議論が続いています。',
    gameNote:
      '盤上の評定マスは札を引いて出来事が決まる場ですが、実際の評定は議論と合意によって物事を決める場であり、偶然で決まるものではありません。ゲームの都合で「その年に何が起きたか」を引き当てる仕組みに置き換えています。',
  },
};

/**
 * 25 敦賀湊（湊）。
 * 越前国敦賀郡。日本海側の物資が京へ入る玄関口。
 */
export const TSURUGA_MINATO: SquareBase = {
  id: 25,
  kind: 'port',
  name: '敦賀湊',
  reading: 'つるがみなと',
  region: 'hokuriku',
  note: {
    summary:
      '日本海に開いた越前の湊。北国の米や海産物がここで陸揚げされ、峠を越えて琵琶湖へ、さらに船で京へ運ばれた。',
    detail: {
      1: 'いまの福井県敦賀市にあった港です。日本海をわたってきた船が、北のくにから米や魚をはこんできました。荷物はここで陸にあげられ、山をこえて琵琶湖にはこばれ、船にのせかえて京へとどきました。港の町には荷物をあつかう店がならび、大きくにぎわいました。',
      2: '越前国敦賀郡の湊。日本海に深く入り込んだ湾が天然の良港となり、北陸・出羽・蝦夷地の産物が集まった。荷は敦賀で陸揚げされ、深坂峠を越えて琵琶湖北岸の塩津へ、あるいは七里半越で海津へ運ばれ、そこから湖上の船で大津・坂本へ、さらに京へ届けられた。この経路は古代からのもので、中世には問（問丸）と呼ばれる荷の保管・運送・仲介を担う商人が育った。町は気比神宮の門前から始まり、しだいに湊町の性格を強めた。戦国期は朝倉氏の支配下にあり、1570年には織田信長がここまで攻め入っている（金ヶ崎の退き口）。',
      3: '越前国敦賀郡の湊町。若狭湾の奥に位置し、冬の季節風を避けられる良港であったため、古代から北国の貢納物の陸揚げ地とされた。敦賀で陸揚げした荷は、深坂越（塩津街道）で琵琶湖北岸の塩津へ、あるいは疋田から七里半越（山中峠）で海津へ運ばれ、湖上を大津・堅田まで船で送り、そこから京・大坂へ入るという中継路が中世を通じて機能した。七里半の名は疋田から海津までの道のりが約七里半あったことによる。運ばれたのは米・材木・塩・鉄のほか、北方からの鮭・鰊・昆布などで、京の食文化を支えた。荷の保管・運送・売買を仲介する問（問丸）が多数成立し、湊には津料（入港税）が課され、気比神宮など寺社もその権益に関わった。町は気比神宮の門前町として出発し、のち湊町の色を濃くする。戦国期には朝倉氏が敦賀郡司家を置いて支配し、1570年に織田信長が手筒山城・金ヶ崎城を攻め落としたが、浅井長政の離反により撤退している（金ヶ崎の退き口）。1573年の朝倉氏滅亡後は織田の支配下に入り、のち大谷吉継が城主となった。江戸時代に北前船の航路が整うと敦賀はさらに栄えるが、それは中世の中継路の延長線上にある。',
    },
    year: [1400, 1600],
    keywords: ['敦賀', '日本海交易', '問丸', '琵琶湖水運', '気比神宮', '朝倉氏'],
    caveat:
      '昆布や鰊が大量に流通したのは、航路と船が整った江戸時代の北前船の時代が中心です。中世にも北方の産物は入っていましたが、量や頻度は史料からは細かく確かめられません。',
    gameNote:
      '盤上では湊マスで石高と銭を交換し、第III章以降は鉄砲を扱えますが、実際の敦賀は日本海と京を結ぶ中継地で、南蛮船が直接来る港ではありませんでした。鉄砲の取引は遊びの都合で全国の湊に共通の仕組みとしています。',
  },
};

/**
 * 31 厳島（合戦）。第II章の固定合戦イベント。
 * 神域であることと、寡兵が大軍を破ったとされる合戦の両方を書く。
 */
export const ITSUKUSHIMA: SquareBase = {
  id: 31,
  kind: 'battle',
  name: '厳島',
  reading: 'いつくしま',
  region: 'sanyo',
  note: {
    summary:
      '瀬戸内海の島。海上に社殿を構える厳島神社は航海の守り神として信仰を集めた。1555年、毛利元就と陶晴賢がここで戦った。',
    detail: {
      1: 'いまの広島県にある、海にうかぶ島です。海の中に鳥居が立つ厳島神社があり、船で行き来する人たちがぶじをいのりました。1555年、毛利元就がこの島で陶晴賢とたたかい、少ない兵で勝ったと伝えられています。',
      2: '安芸国の島で、宗像三女神をまつる厳島神社がある。島そのものが神の宿る場所とされ、平清盛が12世紀に海上へ張り出す社殿を整えてから、瀬戸内海を行き来する船乗りや商人の信仰を広く集めた。門前には市が立ち、参詣の人でにぎわった。1555年、毛利元就は主家の大内氏を1551年に倒していた陶晴賢をこの島に誘い込み、対岸から渡海して背後を突いたとされる戦いで勝利した。この結果、毛利氏は中国地方で優位に立つことになる。',
      3: '安芸国佐西郡の島。市杵島姫命ら宗像三女神をまつる厳島神社が鎮座し、島全体が神域とみなされたため、島内での出産や埋葬が忌まれた。12世紀に平清盛が海上に張り出す現在の形の社殿を整えて以降、瀬戸内海の海上交通の守護神として、水軍・廻船商人・参詣者の信仰を集めた。門前には市が立ち、社領からの上がりと参詣者が島の経済を支えている。1555年（天文24年）10月、毛利元就と陶晴賢がこの島で戦った。陶晴賢は1551年に主君大内義隆を討って大内家の実権を握った人物で、元就はこれに離反し、島に宮尾城を築いて陶方を渡海させ、悪天候の夜に対岸から兵を渡して背後を突いたと伝えられる。瀬戸内の村上氏の水軍が毛利方に加わり、海上の主導権を握ったことが大きかったとされる。戦いののち、神域を血で汚したとして社殿の清めが行われたという伝えがある。この合戦を機に毛利氏は大内領の継承へ進み、中国地方の主導権を握った。',
    },
    year: 1555,
    keywords: ['厳島', '厳島神社', '毛利元就', '陶晴賢', '瀬戸内海', '村上水軍'],
    caveat:
      '「陶方2万に対して毛利方4千」という兵力差は、江戸時代に成立した軍記物（『陰徳太平記』など）に由来するもので、同時代の史料で裏づけられた数字ではありません。奇襲によって勝ったという筋書きも、後世に整えられた面があります。援軍を出した村上氏についても、来島・能島のどちらが主だったのかをめぐって議論があり、のちの政治的事情で記述が書き換えられた可能性が指摘されています。',
    gameNote:
      '盤上では「寡兵が大軍を破りうる」ことを、投入兵で手勢札の枚数が決まるという仕組みで表しています（docs/06 §4）。実際の勝敗は兵数だけで決まったわけではなく、水軍の協力・天候・陶方の内部事情など複数の要因が挙げられており、確率のゆらぎに置き換えたのはゲームの都合です。',
  },
};

/**
 * 33 博多湊（湊）。
 * 対外交易の窓口。焼けては再建された町でもある。
 */
export const HAKATA_MINATO: SquareBase = {
  id: 33,
  kind: 'port',
  name: '博多湊',
  reading: 'はかたみなと',
  region: 'kyushu',
  note: {
    summary:
      '筑前の湊。古代から大陸への窓口で、中世には日明・日朝貿易の拠点となった。戦乱で何度も焼け、そのたびに商人の手で再建された。',
    detail: {
      1: 'いまの福岡市にある港です。日本の中で大陸にいちばん近く、むかしから中国や朝鮮の船が行き来しました。焼き物や絹、薬などがはこびこまれ、日本からは刀や硫黄などを送りました。戦でなんども焼けましたが、そのたびに商人たちが町をたてなおしました。',
      2: '筑前国の湊町。地理的に大陸へ近く、古代の大宰府の時代から対外交流の窓口だった。中世には宋・元・明の商人が住む町があり、日明貿易（勘合貿易）では堺と並ぶ拠点となった。輸入されたのは生糸・絹織物・陶磁器・書籍・銅銭で、輸出は刀剣・硫黄・扇などである。町は息浜と博多浜に分かれ、大内氏と大友氏がそれぞれ勢力を及ぼした時期がある。神屋・嶋井といった有力商人が育ち、彼らは貿易だけでなく鉱山開発にも関わった。16世紀後半には諸勢力の争奪で町が焼け、1587年の豊臣秀吉による九州平定の後、商人の協力を得て町を区画し直す復興が行われた（太閤町割）。',
      3: '筑前国那珂郡の湊町。大陸との距離が近く、古代の鴻臚館・大宰府以来の対外窓口として機能した。11世紀以降は宋商人の居留地（唐房）が置かれ、日宋・日元貿易の中心となる。1274年・1281年の元軍来襲でも主戦場となった。室町期の日明貿易では、大内氏に結んだ博多商人と細川氏に結んだ堺商人が遣明船の権益を争い、1523年の寧波の乱に至る。1547年の派遣を最後に勘合貿易は途絶え、以後は私貿易や後期倭寇の活動が優勢になった。輸入品は生糸・絹織物・陶磁器・薬種・書籍・銅銭、輸出品は刀剣・硫黄・銅・扇・漆器など。博多商人の神屋寿禎は1520〜30年代に石見銀山の開発と灰吹法の導入に関わったとされ、湊の資本が鉱山を動かしたことを示す例として語られる。町は息浜と博多浜からなり、大内・大友・龍造寺・島津の抗争のなかで焼失を繰り返した。1587年、九州平定を終えた豊臣秀吉は焼け跡の博多に区画整理を命じ、石田三成を奉行、黒田孝高を実務にあたらせ、神屋宗湛・嶋井宗室ら商人の協力で復興を進めた。これが太閤町割と呼ばれる。栄西が開いたと伝わる聖福寺、祇園祭礼の櫛田神社など、寺社も町の骨格をなした。',
    },
    year: [1400, 1600],
    keywords: ['博多', '日明貿易', '勘合貿易', '神屋宗湛', '嶋井宗室', '太閤町割'],
    caveat:
      '神屋寿禎が石見銀山を開いて灰吹法を伝えたという話は、後の時代に書かれた『銀山旧記』などに基づくもので、同時代の史料では細部まで確かめられていません。町の焼失と再建の回数や規模についても、記録が断片的で一致していません。',
    gameNote:
      '盤上では湊マスとして石高と銭の交換や鉄砲の購入を扱いますが、実際の博多の交易は品目も相手も時代によって大きく変わりました。第III章以降に鉄砲が買えるのは、1543年の伝来を遊びの中で体感させるための単純化です。',
  },
};

/**
 * 39 高野山（寺社）。
 * 寺社が土地・人・武力・金融を持つ「勢力」であったことを事実として書く。
 * 価値判断は入れない（docs/05 §4-3）。
 */
export const KOYASAN: SquareBase = {
  id: 39,
  kind: 'temple',
  name: '高野山',
  reading: 'こうやさん',
  region: 'kinai',
  note: {
    summary:
      '紀伊の山上に開かれた真言宗の寺院群。広い寺領と僧兵を持ち、大名の墓所や駆け込み先ともなった、独立した勢力だった。',
    detail: {
      1: 'いまの和歌山県の山の上にある、おてらのあつまりです。空海というお坊さんがひらきました。たくさんの土地をもち、じぶんたちを守る僧兵もいました。戦にやぶれた武士がにげこむこともあり、大名のお墓もならんでいます。明治になるまで、女の人は山の上に入れませんでした。',
      2: '紀伊国の山上に開かれた真言宗の中心寺院群。816年に空海が嵯峨天皇から地を与えられて開いたと伝わる。中世には各地に広大な寺領荘園を持ち、年貢を集め、金融も行い、寺を守るための僧兵（行人方）を抱えた。高野聖と呼ばれる僧が全国を回って寄進を募り、奥之院に遺骨を納める信仰を広めたため、武士や庶民の供養塔が山上に集まった。俗世の権力が及びにくい場とみなされ、罪を得た者が逃げ込むこともあった。1581年に織田信長と対立し、1585年には豊臣秀吉の紀州攻めを受けたが、僧の木食応其が交渉にあたって寺領を安堵され、存続した。',
      3: '紀伊国伊都郡の山上に営まれた真言宗の寺院群。816年に空海が嵯峨天皇から下賜された地に開創したと伝えられ、金剛峯寺を中心に子院が集まった。中世の高野山は宗教施設であると同時に、経済的・軍事的な実体を備えた勢力だった。紀伊を中心に広大な寺領荘園を持って年貢を徴収し、米や銭を貸し付ける金融も営み、寺を守るための武力として行人方の僧兵を抱えた。内部は教学を担う学侶方、寺務と武力を担う行人方、勧進と葬送を担う聖方の三方に分かれ、互いに争うこともあった。高野聖は全国を巡って寄進を集め、奥之院への納骨供養を広めたため、諸国の武士から庶民までの供養塔が山上に林立することになる。俗権の追及が及ばない「無縁所」とみなされ、敗者や罪を得た者が逃げ込む先ともなった。1581年に織田信長と衝突し、信長方は多数の高野聖を捕らえて処刑したと伝えられるが、1582年の本能寺の変で攻撃は中断した。1585年の豊臣秀吉の紀州攻めでは、僧の木食応其が交渉にあたり、武装解除と引き換えに寺領の一部を安堵されて存続した。応其はのちの復興にも関わっている。1595年には豊臣秀次が高野山で自害しており、山が政治の舞台にもなったことを示す。なお高野山は長く女人禁制で、女性は山上に立ち入れず、七つの登山口に建てられた女人堂までしか進めなかった。禁制は1872年の太政官布告で制度としては廃されたが、高野山で実際に女性の居住が認められるまでにはさらに時間がかかった。',
    },
    year: [816, 1600],
    keywords: ['高野山', '金剛峯寺', '空海', '寺社勢力', '高野聖', '女人禁制'],
    caveat:
      '織田方に処刑された高野聖の人数は数百人とも千人以上とも伝えられ、史料によって差が大きく確定していません。空海による開創の年次や経緯も、後世に整えられた伝承を含みます。女人禁制がいつ実質的に解かれたかについても、1872年の布告をもって解禁とする見方と、明治後期まで事実上続いたとする見方があります。',
    gameNote:
      '盤上では寺社マスとして威信を得たり、災害や一揆を一度無効にできたりしますが、これは寺社が持っていた社会的な重みと「駆け込み先」としての性格を、遊びの効果に置き換えたものです。宗教の教えの正しさや善悪を表すものではありません。',
  },
};

/** 本ファイルが担当する特殊マス。盤面組み立て用。 */
export const SPECIAL_WEST_SQUARES: readonly SquareBase[] = [
  KIKIN,
  HYOJO,
  TSURUGA_MINATO,
  ITSUKUSHIMA,
  HAKATA_MINATO,
  KOYASAN,
];
